import { Box, Container, Link, SimpleGrid, Stack, Text } from '@chakra-ui/react'
import { useStartNewCuideiConsultation } from 'hooks/app/cuidei/useStartNewCuideiConsultation'
import NextLink from 'next/link'
import { FC } from 'react'

import CuideiLogo from './CuideiLogo'

interface CuideiFooterProps {
  maxW?: string
  /** The Google Ads clone of a landing says "tratamento", never "prescrição" (PRD 04/11, section 10). */
  variant?: 'seo' | 'gads'
}

interface FooterLink {
  label: string
  href: string
  /** Open in a new tab (legal pages). */
  external?: boolean
}

// cuidei.com.br/ is the conditions hub; "Como funciona" (pharmacy journey) moved to /farma.
const navItems: FooterLink[] = [
  { label: 'Início', href: '/' },
  { label: 'Todas as condições', href: '/#condicoes' },
  { label: 'Como funciona', href: '/farma#como-funciona' }
]

const complianceItems: FooterLink[] = [
  { label: 'Termos e Condições', href: '/termos-e-condicoes', external: true },
  { label: 'Política de Privacidade', href: '/politica-de-privacidade', external: true },
  { label: 'LGPD · Sigilo médico', href: '/politica-de-privacidade', external: true }
]

const FooterLinkItem: FC<{ item: FooterLink }> = ({ item }) =>
  item.external ? (
    <Link href={item.href} isExternal fontSize="sm" color="cuidei.muted" _hover={{ color: 'cuidei.60' }}>
      {item.label}
    </Link>
  ) : (
    <NextLink href={item.href} passHref legacyBehavior>
      <Link fontSize="sm" color="cuidei.muted" _hover={{ color: 'cuidei.60' }}>
        {item.label}
      </Link>
    </NextLink>
  )

/** Compliance-forward footer (matches the mock): brand + Navegação + Compliance columns, all linked. */
const CuideiFooter: FC<CuideiFooterProps> = ({ maxW = 'container.sm', variant = 'seo' }) => {
  const startNewConsultation = useStartNewCuideiConsultation()

  return (
    <Box as="footer" bg="white" borderTop="1px solid" borderColor="cuidei.20" py={{ base: 8, md: 12 }}>
      <Container maxW={maxW}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 8, md: 12 }}>
          <Stack spacing={4} maxW="sm">
            <NextLink href="/" passHref legacyBehavior>
              <Link _hover={{ textDecoration: 'none' }}>
                <CuideiLogo />
              </Link>
            </NextLink>
            <Text fontSize="sm" color="cuidei.muted">
              Consulta médica online por vídeo.{' '}
              {variant === 'gads'
                ? 'O tratamento depende da avaliação médica individual.'
                : 'A prescrição depende da avaliação individual de um médico.'}
            </Text>
          </Stack>

          <Stack spacing={3}>
            <Text fontWeight={700} color="cuidei.ink">
              Navegação
            </Text>
            {navItems.map((item) => (
              <FooterLinkItem key={item.label} item={item} />
            ))}
            <Link
              as="button"
              textAlign="left"
              fontSize="sm"
              color="cuidei.muted"
              _hover={{ color: 'cuidei.60' }}
              onClick={startNewConsultation}
            >
              Iniciar consulta
            </Link>
          </Stack>

          <Stack spacing={3}>
            <Text fontWeight={700} color="cuidei.ink">
              Compliance
            </Text>
            {complianceItems.map((item) => (
              <FooterLinkItem key={item.label} item={item} />
            ))}
          </Stack>
        </SimpleGrid>

        <Text fontSize="xs" color="cuidei.muted" mt={{ base: 8, md: 10 }}>
          © 2026 Cuidei · Todos os direitos reservados
        </Text>
      </Container>
    </Box>
  )
}

export default CuideiFooter
